import { Injectable } from "@angular/core";
import { TraitementsLexiClient, TraitementDto, TypeTraitement, ResultatTraitement } from '@lexi-clients/lexi';
import { lastValueFrom } from "rxjs";
import { map } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class TraitementsService {
  constructor(private readonly traitementsLexiClient: TraitementsLexiClient) {}

  getById(traitementId: number): Promise<TraitementDto> {
    return lastValueFrom(
      this.traitementsLexiClient
        .getTraitementsById([traitementId])
        .pipe(map((traitements) => (traitements ? traitements[0] : null))),
    );
  }

  getLatestByItemId(itemId: string): Promise<TraitementDto> {
    const latest = true;
    return lastValueFrom(
      this.traitementsLexiClient
        .getTraitements(null, null, null, itemId, latest)
        .pipe(map((traitements) => (traitements ? traitements[0] : null))),
    );
  }

  getLatestByType(typeTraitement: TypeTraitement): Promise<TraitementDto> {
    return lastValueFrom(
      this.traitementsLexiClient.getLatestTraitementByType(typeTraitement),
    );
  }

  readMetadataTextValue(metadataXml: string, propertyName: string): string {
    const doc: Document = new DOMParser().parseFromString(
      metadataXml,
      "application/xml",
    );
    return doc.querySelector(propertyName)?.textContent;
  }

  readMetadataRaison(traitement: TraitementDto): string {
    return this.readMetadataTextValue(traitement.metadata, "Raison");
  }

  /**
   * Scrute un traitement en vérifiant son statut à intervalles réguliers.
   * @param traitementId
   * @param delayInMs intervalle de scrutation en ms
   * @param maxNbErrors maximum de tentatives avant d'abandonner (en cas d'erreur réseau ou serveur)
   * @returns résultat du traitement
   * @throws Erreur si le nombre maximum d'erreurs est atteint
   */
  scruterTraitement(
    traitementId: number,
    delayInMs: number = 5000,
    maxNbErrors: number = 5,
  ) {
    return new Promise<ResultatTraitement>((resolve, reject) => {
      let nbErrors = 0;
      const interval = setInterval(async () => {
        try {
          const res = await this.getById(traitementId);
          if (
            res.resultat == ResultatTraitement.succes ||
            res.resultat == ResultatTraitement.echec
          ) {
            clearInterval(interval);
            resolve(res.resultat);
          }
        } catch {
          nbErrors++;
          if (nbErrors > maxNbErrors) {
            clearInterval(interval);
            reject(
              new Error(`Erreur après plusieurs tentatives (${maxNbErrors})`),
            );
          }
        }
      }, delayInMs);
    });
  }

  async getLatestErrorReasonByItemId(itemId: string): Promise<string> {
    const traitement = await this.getLatestByItemId(itemId);
    if (
      traitement &&
      (traitement.resultat === ResultatTraitement.erreur ||
        traitement.resultat === ResultatTraitement.echec)
    ) {
      return this.readMetadataRaison(traitement);
    }
  }
}
